import { type ChangeEvent, type CSSProperties, type MouseEvent, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import { getLocalStorageGlobalListPageLimit, setLocalStorageGlobalListPageLimit } from '../../components';
import { usePDFContext } from '../../contexts';
import type { Order, TrackedRef } from '../../models';

export const usePagination = <TSortField extends string>(
  toggleRefresh: () => void,
  initialOrderBy: TSortField,
  initialOrder: Order = 'asc',
  isNotGlobal = false,
  initialLimit = 10
) => {
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(isNotGlobal ? initialLimit : getLocalStorageGlobalListPageLimit());
  const [count, setCount] = useState<number>(0);
  const [order, setOrder] = useState<Order>(initialOrder);
  const [orderBy, setOrderBy] = useState<TSortField>(initialOrderBy);

  const handlePageChange = (_event: any, newPage: number): void => {
    setPage(newPage);
    toggleRefresh();
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLimit(parseInt(event.target.value, 10));
    if (!isNotGlobal) {
      setLocalStorageGlobalListPageLimit(event.target.value);
    }
    setPage(0);
    toggleRefresh();
  };

  const handleRequestSort = (_event: MouseEvent, property: TSortField): void => {
    const isAsc = orderBy === property && order.includes('asc');
    // change the order
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    toggleRefresh();
  };

  return {
    count,
    setCount,
    page,
    setPage,
    limit,
    setLimit,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  };
};

/**
 * A factory hook to track individual htmlDiv elements with ref and apply styles, using PDFContext, keeping track of global page height and paginating content appropriately
 */
export const usePdfPaginationMulti = () => {
  const { registerElement } = usePDFContext();

  const createTrackedRef = (initialMarginTop: number = 0): TrackedRef => {
    const ref = useRef<HTMLDivElement>(null);
    const [style, setStyle] = useState<CSSProperties>({});

    useEffect(() => {
      if (!ref.current) return;
      const el = ref.current;
      const marginToApply = registerElement(el, initialMarginTop);
      // console.log('>>>, Margin To Apply:', marginToApply);
      setStyle({
        marginTop: marginToApply
      });
      //todo:v2-changes
    }, []);

    return { ref, style };
  };

  return { createTrackedRef };
};

/**
 * Hook to track pages updates through React Router (SPA) and send data to Google Analytics
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (!ReactGA.isInitialized) return;
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
      title: document.title
    });
  }, [location]);
};
